import type { ReactNode } from 'react';
import type React from 'react';
import type { DraggableProvided } from 'react-beautiful-dnd';
import { Draggable } from 'react-beautiful-dnd';
import {
  RiDeleteBinLine,
  RiDownload2Line,
  RiMoreFill,
  RiEyeOffLine,
  RiEyeLine,
  RiChat4Line,
  RiChatOffLine,
  RiCameraOffLine,
  RiCameraLine,
  RiIndeterminateCircleLine,
  RiHammerLine,
  InputCheckBox
} from '@itandi/itandi-bb-ui';
import clsx from 'clsx';
import { useSWRConfig } from 'swr';
import { useRouter } from 'next/router';
import { useOpenHandler } from '~/hooks/useOpenHandler';
import Styles from './ReportImagesAreaSpRow.module.scss';
import type { DealReportImage } from '~/models/DealReportImage';
import {
  operableReportImage,
  imageUploadedLabel
} from '~/models/DealReportImage';
import { ImagePreviewModal } from '~/components/common/parts/modal/ImagePreviewModal';
import { HideInputForm } from '~/components/common/parts/input/HideInputForm';
import type { NskResponse } from '~/models/Api';
import { ConfirmModal } from '~/components/common/parts/modal/ConfirmModal';
import { Dropdown } from '~/components/common/parts/dropdown/Dropdown';
import { DropdownList } from '~/components/common/parts/dropdown/DropdownList';
import { DropdownMenu } from '~/components/common/parts/dropdown/DropdownMenu';
import { getReportImageSwrKey } from '~/utils/DealReportImageUtil';

type Props = {
  reportImage: DealReportImage;
  handleDownloadImage: (reportImage: DealReportImage) => void;
  index: number;
  handleUpdateSeq:
    | ((newOrderImages: DealReportImage[]) => Promise<void>)
    | null;
  handleUpdate: (submitValue: DealReportImage) => Promise<NskResponse<void>>;
  handleDestroy:
    | ((reportImage: DealReportImage) => Promise<NskResponse<void>>)
    | null;
  companyType: 'company' | 'vendor';
  displaySeqNo: number | null;
  onChangeSelectedImage: (reportImageId: number) => void;
  selectedImageIds: ReadonlyArray<number>;
};

const renderView = (content: string | null | undefined): ReactNode => (
  <div className={Styles.ContentView}>
    {content == null || content === '' ? (
      <span className={Styles.YetInput}>未入力</span>
    ) : (
      <span>{content}</span>
    )}
  </div>
);

export const ReportImagesAreaSpRow: React.FC<Props> = ({
  reportImage,
  index,
  handleDownloadImage,
  handleUpdateSeq,
  handleUpdate,
  handleDestroy,
  companyType,
  displaySeqNo,
  onChangeSelectedImage,
  selectedImageIds
}: Props) => {
  const router = useRouter();
  const { mutate: globalMutate } = useSWRConfig();
  const { isOpen, handleOpen, handleClose } = useOpenHandler();
  const {
    isOpen: isOpenDelete,
    handleOpen: handleOpenDelete,
    handleClose: handleCloseDelete
  } = useOpenHandler();

  const handleMutateReportImages = async (): Promise<void> => {
    await globalMutate(getReportImageSwrKey(router.asPath, reportImage.dealId));
  };

  const isVendor = companyType === 'vendor';
  const operableImage = operableReportImage(reportImage, companyType);
  const isShared = reportImage.shared;
  const isOutputPdfImage = reportImage.outputPdfImage;
  const isOutputPdfNote = reportImage.outputPdfNote;
  const uploadLabel = imageUploadedLabel(isVendor, reportImage);
  const submitPoint = async (submitValue: string): Promise<void> => {
    const newReportImage = { ...reportImage, point: submitValue };
    await handleUpdate(newReportImage);
    await handleMutateReportImages();
  };
  const submitNote = async (submitValue: string): Promise<void> => {
    const newReportImage = { ...reportImage, note: submitValue };
    await handleUpdate(newReportImage);
    await handleMutateReportImages();
  };
  const onClickImage = (e: React.MouseEvent): void => {
    e.preventDefault();
    handleOpen();
  };
  const handleAcceptDestroy = async (): Promise<void> => {
    await handleDestroy?.(reportImage);
    await handleMutateReportImages();
  };
  const handleAcceptUpdateShared = async (
    submitValue: boolean
  ): Promise<void> => {
    const newReportImage = { ...reportImage, shared: submitValue };
    await handleUpdate(newReportImage);
    await handleMutateReportImages();
  };
  const handleAcceptUpdateOutputPdf = async (
    submitKey: 'outputPdfImage' | 'outputPdfNote',
    submitValue: boolean
  ): Promise<void> => {
    const newReportImage = { ...reportImage, [submitKey]: submitValue };
    await handleUpdate(newReportImage);
    await handleMutateReportImages();
  };
  const handleAcceptUpdateConstruction = async (
    submitValue: 'unspecified' | 'targeted'
  ): Promise<void> => {
    await handleUpdate({ ...reportImage, construction: submitValue });
    await handleMutateReportImages();
  };
  return (
    <Draggable
      key={reportImage.id}
      draggableId={String(reportImage.id)}
      index={index}
      isDragDisabled={handleUpdateSeq === null}
    >
      {(provided: DraggableProvided): React.ReactElement<HTMLElement> => (
        <div
          className={clsx(
            reportImage.construction === 'targeted' && Styles.Targeted
          )}
        >
          <div
            ref={provided.innerRef}
            className={Styles.ReportImageRow}
            style={provided.draggableProps.style}
            {...provided.draggableProps}
          >
            <div
              className={Styles.DragHandleWrapper}
              {...provided.dragHandleProps}
            >
              <img alt="drag handle" src="/assets/DragHandle.svg" />
            </div>
            <div className={Styles.ImageBlock}>
              <a href="" onClick={onClickImage}>
                <img
                  alt="写真"
                  className={Styles.Image}
                  src={
                    reportImage.thumbnailImageUrl === null
                      ? reportImage.imageUrl
                      : reportImage.thumbnailImageUrl
                  }
                />
              </a>
              <div className={Styles.Plates}>
                {(isVendor || (!isVendor && isOutputPdfImage)) && (
                  <div className={Styles.Plate}>No.{displaySeqNo}</div>
                )}
                {uploadLabel && (
                  <div className={Styles.Plate}>{uploadLabel}</div>
                )}
              </div>
              {isVendor && !isShared && (
                <p className={Styles.NotSharedPlate}>
                  管理会社に共有されません
                </p>
              )}
              {!isVendor && !isOutputPdfImage && (
                <p className={Styles.NotOutPutPdf}>
                  写真は報告書に出力されません
                </p>
              )}
              <p className={Styles.ImageCheckbox}>
                <InputCheckBox
                  checked={selectedImageIds.includes(reportImage.id)}
                  onChange={(): void => onChangeSelectedImage(reportImage.id)}
                />
              </p>
              <div className={Styles.ButtonWrapper}>
                <Dropdown
                  className={Styles.MenuDropdown}
                  noBorder
                  title={
                    <span className={Styles.MenuButton}>
                      <RiMoreFill className={Styles.MenuIcon} />
                    </span>
                  }
                  useCaret={false}
                  variant="link"
                >
                  <DropdownList>
                    <DropdownMenu
                      onClick={(): void => handleDownloadImage(reportImage)}
                    >
                      <RiDownload2Line className={Styles.DropdownMenuIcon} />
                      画像をダウンロード
                    </DropdownMenu>
                    {operableImage && (
                      <DropdownMenu
                        onClick={(): void => {
                          handleAcceptUpdateConstruction(
                            reportImage?.construction === 'targeted'
                              ? 'unspecified'
                              : 'targeted'
                          ).then();
                        }}
                      >
                        {reportImage?.construction === 'targeted' ? (
                          <>
                            <RiIndeterminateCircleLine
                              className={Styles.DropdownMenuIcon}
                            />
                            施工推奨から外す
                          </>
                        ) : (
                          <>
                            <RiHammerLine className={Styles.DropdownMenuIcon} />
                            施工推奨にする
                          </>
                        )}
                      </DropdownMenu>
                    )}
                    {isVendor && operableImage && (
                      <DropdownMenu
                        onClick={(): void => {
                          handleAcceptUpdateShared(!reportImage.shared);
                        }}
                      >
                        {isShared ? (
                          <>
                            <RiEyeOffLine className={Styles.DropdownMenuIcon} />
                            写真を共有しない
                          </>
                        ) : (
                          <>
                            <RiEyeLine className={Styles.DropdownMenuIcon} />
                            写真を共有する
                          </>
                        )}
                      </DropdownMenu>
                    )}
                    {!isVendor && (
                      <DropdownMenu
                        onClick={(): void => {
                          handleAcceptUpdateOutputPdf(
                            'outputPdfImage',
                            !reportImage?.outputPdfImage
                          ).then();
                        }}
                      >
                        {isOutputPdfImage ? (
                          <>
                            <RiCameraOffLine
                              className={Styles.DropdownMenuIcon}
                            />
                            写真を報告書に含めない
                          </>
                        ) : (
                          <>
                            <RiCameraLine className={Styles.DropdownMenuIcon} />
                            写真を報告書に含める
                          </>
                        )}
                      </DropdownMenu>
                    )}
                    {!isVendor && (
                      <DropdownMenu
                        className={!isOutputPdfImage ? Styles.disabled : ''}
                        onClick={(): void => {
                          handleAcceptUpdateOutputPdf(
                            'outputPdfNote',
                            !reportImage?.outputPdfNote
                          ).then();
                        }}
                      >
                        {isOutputPdfNote ? (
                          <>
                            <RiChatOffLine
                              className={Styles.DropdownMenuIcon}
                            />
                            備考を報告書に含めない
                          </>
                        ) : (
                          <>
                            <RiChat4Line className={Styles.DropdownMenuIcon} />
                            備考を報告書に含める
                          </>
                        )}
                      </DropdownMenu>
                    )}
                    {operableImage && (
                      <DropdownMenu onClick={handleOpenDelete}>
                        <span className={Styles.DeleteLink}>
                          <RiDeleteBinLine
                            className={Styles.DropdownMenuIcon}
                          />
                          画像を削除
                        </span>
                      </DropdownMenu>
                    )}
                  </DropdownList>
                </Dropdown>
              </div>
              <ConfirmModal
                confirmType="delete"
                handleAccept={handleAcceptDestroy}
                handleCancel={handleCloseDelete}
                isOpen={isOpenDelete}
              >
                この画像を削除してよろしいですか？
              </ConfirmModal>
              <ImagePreviewModal
                handleClose={handleClose}
                isOpen={isOpen}
                reportImage={reportImage}
              />
            </div>
            <div className={Styles.RemarksBlock}>
              {/* <div className={Styles.No}>No.{index + 1}</div> */}
              {reportImage.construction === 'targeted' && (
                <div className={Styles.RemarkRow}>
                  <div className={Styles.ConstructionState}>施工推奨箇所</div>
                </div>
              )}
              <div className={Styles.RemarkRow}>
                <div className={Styles.Label}>撮影場所</div>
                <div className={Styles.Content}>
                  {operableImage ? (
                    <>
                      <HideInputForm
                        handleSubmit={submitPoint}
                        value={reportImage.point ?? ''}
                        viewComponent={renderView(reportImage.point)}
                      />
                    </>
                  ) : (
                    <>{renderView(reportImage.point)}</>
                  )}
                </div>
              </div>
              <div className={Styles.RemarkRow}>
                <div className={Styles.Label}>備考</div>
                <div className={Styles.Content}>
                  {operableImage ? (
                    <>
                      <HideInputForm
                        as="textarea"
                        handleSubmit={submitNote}
                        value={reportImage.note ?? ''}
                        viewComponent={renderView(reportImage.note)}
                      />
                    </>
                  ) : (
                    <>{renderView(reportImage.note)}</>
                  )}
                  {!isVendor && isOutputPdfImage && !isOutputPdfNote && (
                    <p className={Styles.NotOutPutPdfNote}>
                      備考は報告書に出力されません
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
};
